import React from 'react';
import { Filter, Project } from '../types';
import { Search, Tag, Users, Calendar, Clock, DollarSign, Star } from 'lucide-react';

interface FilterPanelProps {
  data: Project[];
  onFilterChange: (filter: Filter) => void;
}

export function FilterPanel({ data, onFilterChange }: FilterPanelProps) {
  const [filter, setFilter] = React.useState<Filter>({
    projectName: '',
    customerName: '',
    selectedTags: [],
    selectedStaff: [],
    year: '',
    duration: '',
    budget: '',
    featured: false
  });

  const uniqueCustomers = React.useMemo(() => {
    const customerSet = new Set<string>();
    data.forEach(project => {
      if (project.customer?.name) {
        customerSet.add(project.customer.name);
      }
    });
    return Array.from(customerSet).sort();
  }, [data]);

  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>();
    data.forEach(project => {
      project.tags.forEach(tag => tagSet.add(tag.name));
    });
    return Array.from(tagSet).sort();
  }, [data]);

  const allStaff = React.useMemo(() => {
    const staffSet = new Set<string>();
    data.forEach(project => {
      project.staff.forEach(member => staffSet.add(member.name));
    });
    return Array.from(staffSet).sort();
  }, [data]);

  const uniqueYears = React.useMemo(() => {
    const yearSet = new Set<string>();
    data.forEach(project => {
      if (project.year) {
        yearSet.add(project.year);
      }
    });
    return Array.from(yearSet).sort();
  }, [data]);

  const uniqueDurations = React.useMemo(() => {
    const durationSet = new Set<string>();
    data.forEach(project => {
      if (project.duration?.name) {
        durationSet.add(project.duration.name);
      }
    });
    return Array.from(durationSet).sort();
  }, [data]);

  const uniqueBudgets = React.useMemo(() => {
    const budgetSet = new Set<string>();
    data.forEach(project => {
      if (project.budget) {
        budgetSet.add(project.budget);
      }
    });
    return Array.from(budgetSet).sort();
  }, [data]);

  const handleFilterChange = (key: keyof Filter, value: string | string[] | boolean) => {
    const newFilter = { ...filter, [key]: value };
    setFilter(newFilter);
    onFilterChange(newFilter);
  };

  const toggleTag = (tagName: string) => {
    const selectedTags = filter.selectedTags.includes(tagName)
      ? filter.selectedTags.filter(t => t !== tagName)
      : [...filter.selectedTags, tagName];
    handleFilterChange('selectedTags', selectedTags);
  };

  const toggleStaff = (staffName: string) => {
    const selectedStaff = filter.selectedStaff.includes(staffName)
      ? filter.selectedStaff.filter(s => s !== staffName)
      : [...filter.selectedStaff, staffName];
    handleFilterChange('selectedStaff', selectedStaff);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Filters</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Project Name</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search projects..."
              value={filter.projectName}
              onChange={(e) => handleFilterChange('projectName', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Featured Projects</label>
          <div className="mt-1">
            <button
              onClick={() => handleFilterChange('featured', !filter.featured)}
              className={`inline-flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
                filter.featured
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Star className={`w-4 h-4 mr-2 ${filter.featured ? 'fill-current' : ''}`} />
              {filter.featured ? 'Featured Only' : 'Show All'}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Customer</label>
          <div className="mt-1">
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              value={filter.customerName}
              onChange={(e) => handleFilterChange('customerName', e.target.value)}
            >
              <option value="">All Customers</option>
              {uniqueCustomers.map(customer => (
                <option key={customer} value={customer}>
                  {customer}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Year</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <Calendar className="h-4 w-4 text-gray-400" />
            </div>
            <select
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              value={filter.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
            >
              <option value="">All Years</option>
              {uniqueYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Duration</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <Clock className="h-4 w-4 text-gray-400" />
            </div>
            <select
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              value={filter.duration}
              onChange={(e) => handleFilterChange('duration', e.target.value)}
            >
              <option value="">All Durations</option>
              {uniqueDurations.map(duration => (
                <option key={duration} value={duration}>{duration}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Budget</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <DollarSign className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Filter by budget..."
              value={filter.budget}
              onChange={(e) => handleFilterChange('budget', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
          <div className="flex flex-wrap gap-2">
            {allTags.map(tagName => (
              <button
                key={tagName}
                onClick={() => toggleTag(tagName)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                  filter.selectedTags.includes(tagName)
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Tag className="w-4 h-4 mr-1" />
                {tagName}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Team Members</label>
          <div className="flex flex-wrap gap-2">
            {allStaff.map(staffName => (
              <button
                key={staffName}
                onClick={() => toggleStaff(staffName)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                  filter.selectedStaff.includes(staffName)
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Users className="w-4 h-4 mr-1" />
                {staffName}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}